import { AdminPageHeader } from "@admin-ui/components/composed/AdminPageHeader";
import { AdminLayout } from "@admin-ui/components/frames/AdminLayout";
import { Head, useForm } from "@inertiajs/react";
import { adminProgramsPath } from "@travily/routes";
import type AdminProgramsNew from "@travily/types/pages/AdminProgramsNew";
import { type ReactNode, useCallback } from "react";
import { useAdminLayoutAccount } from "@/hooks/admin/use-admin-layout-account";
import { useDirectUploadMultiple } from "@/hooks/use-direct-upload-multiple";
import AdminPersistentLayout from "@/layouts/admin-persistent-layout";
import { NewView } from "./new.view";

type AdminProgramsNewPageProps = AdminProgramsNew;

interface NewProgramFormData {
  account_id: string;
  name: string;
  website_url: string;
}

const PDF_MAX_BYTES = 10 * 1024 * 1024;

function AdminProgramsNewPage({ available_accounts }: AdminProgramsNewPageProps) {
  const account = useAdminLayoutAccount();
  const pdfUpload = useDirectUploadMultiple();

  const form = useForm<NewProgramFormData>({
    account_id: String(available_accounts[0]?.id ?? ""),
    name: "",
    website_url: "",
  });

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      form.transform((data) => {
        const sources: Array<{ kind: "website" | "pdf"; url?: string; signed_id?: string }> = [];
        if (data.website_url.trim()) sources.push({ kind: "website", url: data.website_url.trim() });
        for (const id of pdfUpload.completedSignedIds) sources.push({ kind: "pdf", signed_id: id });

        return {
          program: { account_id: Number(data.account_id), name: data.name },
          sources,
        };
      });
      form.post(adminProgramsPath(), { preserveScroll: "errors" });
    },
    [form, pdfUpload.completedSignedIds],
  );

  const onPdfFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    const valid = files.filter((f) => f.type === "application/pdf" && f.size <= PDF_MAX_BYTES);
    if (valid.length > 0) pdfUpload.addFiles(valid);
    e.target.value = "";
  };

  const accountOptions = available_accounts.map((account) => ({
    value: String(account.id),
    label: account.display_name ?? `@${account.handle ?? "?"}`,
  }));

  return (
    <AdminLayout activeUrl={adminProgramsPath()} account={account}>
      <Head title="New program - Admin" />

      <AdminPageHeader
        breadcrumbs={[{ label: "Programs", href: adminProgramsPath() }, { label: "New" }]}
        title="New program"
        subtitle="Pick an account, name the program, and optionally add sources for AI to pre-fill the details."
      />

      <NewView
        accountOptions={accountOptions}
        accountId={form.data.account_id}
        name={form.data.name}
        websiteUrl={form.data.website_url}
        nameError={form.errors.name}
        pdfFiles={pdfUpload.files}
        isSubmitDisabled={form.processing || pdfUpload.isUploading}
        cancelHref={adminProgramsPath()}
        onSubmit={handleSubmit}
        onAccountIdChange={(value) => form.setData("account_id", value)}
        onNameChange={(value) => {
          form.setData("name", value);
          form.clearErrors("name");
        }}
        onWebsiteUrlChange={(value) => form.setData("website_url", value)}
        onPdfFilesChange={onPdfFiles}
        onRemovePdf={pdfUpload.removeFile}
      />
    </AdminLayout>
  );
}

AdminProgramsNewPage.layout = (page: ReactNode) => <AdminPersistentLayout>{page}</AdminPersistentLayout>;

export default AdminProgramsNewPage;
